import {
  Divider,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Tab,
  Tabs,
  styled,
} from '@mui/material';
import { platformNameConverter, stringSlugify } from '../common-utilities';
import { type SyntheticEvent, useState } from 'react';
import type {
  IAppDetailsAppData,
  IDynamicContent,
  ITranslatedTextAll,
  IWidget,
} from '../common-types';
import { PlatformAccreditations } from './PlatformAccreditations';
import { PlatformGallery } from './PlatformGallery';
import { PlatformGeneralFeatures } from './PlatformGeneralFeatures';
import { PlatformInformationSlice } from './PlatformInformationSlice';
import { PlatformScoreDetails } from './PlatformScoreDetails';
import { PlatformScoreSummary } from './PlatformScoreSummary';
import { PlatformThresholdSummary } from './PlatformThresholdSummary';
import { TabPanel, customColorPalette } from '@orcha/micro-library-core-ui';
import { DtacScoreDetails } from '../DtacScoreDetails';
import type { ImageProps } from 'next/image';
import { AccreditationTabs } from './AccreditationTabs';

const Container = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(4)} 0`,
}));

const a11yProps = (index: number, value: string) => {
  return {
    id: `platforms-tab-${index}-${value}`,
    'aria-controls': `platforms-tab-${index}-${value}`,
  };
};

const platformWidgetSelector = {
  scoreSummary: PlatformScoreSummary,
  accreditations: PlatformAccreditations,
  gallery: PlatformGallery,
  informationSlice: PlatformInformationSlice,
  scoreDetails: PlatformScoreDetails,
  generalFeatures: PlatformGeneralFeatures,
  dtacScoreDetails: DtacScoreDetails,
  platformThresholdSummary: PlatformThresholdSummary,
  accreditationTabs: AccreditationTabs,
};

type PlatformTabsProps = {
  app: IAppDetailsAppData;
  widgets?: IWidget[];
  locale: string;
  eventHandlers: {
    handlePlatformTabChange: (platformName: string) => void;
    handleScoreTabChange?: (tabName: string) => void;
    requestDynamicContent: (reviewId: number) => void;
  };
  dynamicContent?: IDynamicContent;
  dynamicContentApiUrl?: string;
  fallbackImageURL?: string;
  translatedTextAll: ITranslatedTextAll;
  FlexibleImageComponent: React.ComponentType<ImageProps>;
};

/**
 * PlatformTabs from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param PlatformTabsProps
 * @param locale is required
 * @param FlexibleImageComponent is required, this is the component that is used to display the image.
 * @param translatedTextAll is required
 */
export const PlatformTabs = ({
  app,
  widgets,
  eventHandlers,
  dynamicContent,
  locale,
  dynamicContentApiUrl,
  fallbackImageURL = '',
  translatedTextAll,
  FlexibleImageComponent,
  ...rest
}: PlatformTabsProps) => {
  const { handlePlatformTabChange } = eventHandlers ?? {};
  const [selectedTab, setSelectedTab] = useState(0);
  const hasWidgets = widgets && widgets.length > 0;
  const { platforms } = app ?? {};
  const orderedWidgets = widgets?.slice().sort((a, b) => a.order - b.order);
  const hasPlatforms = platforms && platforms.length > 0;

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    const selectedPlatform = platforms?.[newValue];
    if (handlePlatformTabChange && selectedPlatform) {
      handlePlatformTabChange(selectedPlatform.platformName);
    }
    setSelectedTab(newValue);
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const tabIndex = event.target.value as unknown as number;
    const selectedPlatform = platforms?.[tabIndex];
    if (handlePlatformTabChange && selectedPlatform) {
      handlePlatformTabChange(selectedPlatform.platformName);
    }
    setSelectedTab(tabIndex);
  };

  return (
    <>
      {hasPlatforms && hasWidgets && (
        <Container {...rest}>
          <Select
            sx={{
              display: {
                xs: 'block',
                sm: 'none',
                marginBottom: (theme) => theme.spacing(2),
              },
            }}
            value={selectedTab.toString()}
            onChange={handleSelectChange}
          >
            {platforms.map((platform, index) => {
              return (
                <MenuItem key={`${platform.platformName}`} value={index}>
                  {platform.platformName}
                </MenuItem>
              );
            })}
          </Select>
          <Tabs
            sx={{ display: { xs: 'none', sm: 'flex' } }}
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {platforms.map((platform, index) => {
              return (
                <Tab
                  key={`${platform.platformName}`}
                  label={platform.platformName}
                  data-testid={`app-details-platform-tab-${stringSlugify(
                    platform.platformName
                  )}`}
                  {...a11yProps(index, platform.platformName)}
                />
              );
            })}
          </Tabs>
          <Divider
            sx={{
              borderBottomWidth: '4px',
              margin: '0 -32px',
              borderColor: customColorPalette.blueGreyLight,
            }}
          />
          {platforms.map((platform, platformIndex) => {
            return (
              <TabPanel
                key={`${platform.platformName}`}
                value={selectedTab}
                index={platformIndex}
              >
                <>
                  {orderedWidgets?.map((widget, index) => {
                    const WidgetComponent = (platformWidgetSelector as any)[
                      widget.name
                    ];
                    return WidgetComponent ? (
                      <>
                        <WidgetComponent
                          platformName={platformNameConverter(
                            platform.platformName
                          )}
                          app={app}
                          configuration={widget?.configuration}
                          eventHandlers={eventHandlers}
                          dynamicContentApiUrl={dynamicContentApiUrl}
                          data-testid={`app-details-platform-tab-widget-${widget.order}`}
                          key={`${widget.name}-${widget.order}`}
                          fallbackImageURL={fallbackImageURL}
                          locale={locale}
                          dynamicContent={dynamicContent}
                          translatedText={translatedTextAll}
                          FlexibleImageComponent={FlexibleImageComponent}
                        />
                        {index + 1 < widgets?.length && <Divider />}
                      </>
                    ) : undefined;
                  })}
                </>
              </TabPanel>
            );
          })}
        </Container>
      )}
    </>
  );
};
